import type { ExploreMessages } from "../sections/explore";

export const explore: ExploreMessages = {
  refresh: "Refresh",

  loadFailed: {
    logs: "Failed to load logs",
    logCount: "Failed to count log records",
    logRecord: "Failed to load the log record",
    traces: "Failed to load traces",
    trace: "Failed to load trace",
    volume: "Failed to summarize log volume",
    observedKeys: "Failed to load observed keys",
  },

  listError: {
    title: "This list could not be loaded.",
    retry: "Try again",
  },

  errorSearchTruncated:
    "The search stopped before reaching the end of the window — there may be older failed traces "
    + "than the ones below. Narrow the time range or the source to search a smaller window.",

  rail: {
    scopeGroup: "SCOPE",
    sourceGroup: "SOURCE",
    timeGroup: "TIME",
    severityGroup: "SEVERITY",
    messageGroup: "MESSAGE",
    statusGroup: "STATUS",
    attributesGroup: "ATTRIBUTES",
    allApplications: "All applications",
    allNamespaces: "All namespaces",
    allEnvironments: "All environments",
    allServices: "All services",
    traceScope: shortId => `Trace: ${shortId}…`,
    leaveTrace: "Leave the trace",
    searchPlaceholder: "Search in message…",
    search: "Search",
    errorsOnly: "Errors only",
  },

  attributes: {
    addFilter: "+ Filter",
    keyPlaceholder: "Filter by key…",
    loadingKeys: "Loading keys…",
    noKeysObserved: "No keys observed.",
    valuePlaceholder: "Value",
    add: "Add",
    cancelFilter: "Cancel filter",
    attributesScope: "Attributes",
    resourceScope: "Resource",
    filterBy: label => `Filter by ${label}`,
    stopFilteringBy: label => `Stop filtering by ${label}`,
    filterByValueTitle: "Filter by this value",
    removeFilterTitle: "Remove this filter",
  },

  logs: {
    title: "Log records",
    loaded: count => `${count} loaded`,
    counted: (shown, total, capped) => `${shown} of ${total}${capped ? "+" : ""} records`,
    follow: "Follow",
    following: "Follow ●",
    headers: {
      time: "TIME",
      severity: "SEVERITY",
      service: "SERVICE",
      tenant: "TENANT",
      message: "MESSAGE",
    },
    loadOlder: "Load older",
    noOlderRecords: "No older records",
  },

  logDetail: {
    title: "Log record",
    rows: {
      time: "Time",
      severity: "Severity",
      service: "Service",
      scope: "Scope",
      span: "Span",
    },
    viewTrace: shortId => `View trace ${shortId}…`,
  },

  sections: {
    message: "Message",
    attributes: "Attributes",
    resource: "Resource",
    events: "Events",
  },

  traces: {
    title: "Traces",
    count: count => `${count} traces`,
    headers: {
      rootSpan: "ROOT SPAN",
      service: "SERVICE",
      started: "STARTED",
      duration: "DURATION",
      spans: "SPANS",
      status: "STATUS",
    },
  },

  traceDetail: {
    loading: "Loading trace…",
    notFound: "Trace not found (or not visible to you).",
    spansCount: count => `${count} spans`,
    errorsCount: count => `${count} ${count === 1 ? "error" : "errors"}`,
    viewCorrelatedLogs: "View correlated logs",
    headers: {
      span: "SPAN",
      timeline: totalMs => `TIMELINE · 0 → ${totalMs} ms`,
      duration: "DURATION",
    },
    rows: {
      span: "Span",
      parent: "Parent",
      kind: "Kind",
      service: "Service",
      start: "Start",
      status: "Status",
    },
  },

  volume: {
    tooWide: "The window is too wide to summarize. Narrow the Time Filter — the list below is unaffected.",
    windowLabel: (from, to, width) => `${from} → ${to} · ${width} buckets`,
    summarizing: "Summarizing volume",
    bucketPartlyInside: span => `${span} · only partly inside the window`,
    bucketElapsing: span => `${span} · still elapsing`,
    bucketCut: span => `${span} · cut by the window`,
    releasesCount: count => `${count} releases`,
  },

  timeFilter: {
    timeRangeAria: "Time range",
    allTime: "All time",
    custom: "Custom…",
    edit: "Edit",
    last: "Last",
    amountAria: "Amount",
    unitAria: "Unit",
    apply: "Apply",
    applyRelativeAria: "Apply relative range",
    applyAbsoluteAria: "Apply absolute range",
    from: "From",
    to: "To",
    toInline: "to",
    cancelAria: "Cancel time range",
    endsBeforeItStarts: "This range ends before it starts.",
    hint: "Empty ends stay open. Times are in your local time zone.",
    fromInstant: time => `From ${time}`,
    untilInstant: time => `Until ${time}`,
    subjects: {
      logRecords: "log records",
      traces: "traces",
    },
    noneInLast: (subject, window) => `No ${subject} in the ${window.toLowerCase()}.`,
    noneIn: (subject, window) => `No ${subject} in ${window}.`,
    noneMatch: subject => `No ${subject} match the current filters.`,
    widenTo: "Widen to",
  },
};
